import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { App as AntdApp, Button, ConfigProvider, Dropdown, Result, Spin, theme, type MenuProps } from "antd";
import { formatMessage, history } from "@umijs/max";
import { Component, Suspense, lazy, useEffect, useMemo, useState, type ErrorInfo, type ReactElement, type ReactNode } from "react";

import defaultSettings from "@/app/default-settings";
import type { PreferenceFormValues } from "../components/AdminPreferenceDrawer";
import { AntdMessageBridge } from "../lib/antd-message-bridge";
import { useAdminLocale } from "../lib/locale";
import type { UserPreferenceRead } from "../lib/auth-api";
import type { PublicSystemConfig } from "../lib/system-api";
import type { UserSession } from "../lib/auth-api";
import {
  AdminShellIcon,
  type AdminLayoutSettings,
  type InitialState,
  type NavThemePreference,
  buildAdminValidateMessages,
  dashboardPath,
  getSystemPrefersDark,
  resolveNavTheme,
} from "./layout-utils";

const ResultView = Result as unknown as (props: Record<string, unknown>) => ReactElement;

type AvatarDropdownProps = {
  children: ReactNode;
  currentUser: UserSession;
  settings: AdminLayoutSettings;
  localeVersion: string;
  onPreferenceApplied: (next: UserPreferenceRead) => void;
  onChangePassword: () => void;
  onLogout: () => void;
};

export type AdminLocaleSyncProps = {
  children: ReactNode;
  publicConfig: PublicSystemConfig | null | undefined;
  setInitialState: (updater: (state: InitialState | undefined) => InitialState) => Promise<void>;
};

type ThemeClassSyncProps = {
  isDarkTheme: boolean;
};

type AdminThemeProviderProps = {
  navTheme: NavThemePreference;
  children: ReactNode;
};

type AdminRuntimeBoundaryState = {
  hasError: boolean;
};

const LazyAdminPreferenceDrawer = lazy(async () => ({
  default: (await import("../components/AdminPreferenceDrawer")).AdminPreferenceDrawer,
}));

function refreshCurrentPage() {
  if (typeof window === "undefined") return;
  window.location.reload();
}

export function AdminLocaleRoot({ children }: { children: ReactNode }) {
  useAdminLocale();
  return <>{children}</>;
}

export function AdminRootProviders({ children }: { children: ReactNode }) {
  useAdminLocale();
  const validateMessages = buildAdminValidateMessages();

  return (
    <ConfigProvider theme={{ cssVar: { key: "exam-admin" } }} form={{ validateMessages }}>
      <AntdApp>
        <AntdMessageBridge />
        {children}
      </AntdApp>
    </ConfigProvider>
  );
}

export class AdminRuntimeBoundary extends Component<{ children: ReactNode }, AdminRuntimeBoundaryState> {
  override state: AdminRuntimeBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {}

  override render() {
    if (this.state.hasError) {
      return (
        <ResultView
          status="500"
          title={formatMessage({ id: "admin.runtimeError.title" })}
          subTitle={formatMessage({ id: "admin.runtimeError.subtitle" })}
          extra={[
            <Button key="refresh" color="primary" variant="solid" onClick={refreshCurrentPage}>
              {formatMessage({ id: "common.actions.refresh" })}
            </Button>,
            <Button key="dashboard" onClick={() => history.push(dashboardPath)}>
              {formatMessage({ id: "admin.accessDenied.backToDashboard" })}
            </Button>,
          ]}
        />
      );
    }

    return this.props.children;
  }
}

function ThemeClassSync({ isDarkTheme }: ThemeClassSyncProps) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;
    root.classList.remove("admin-theme-dark", "admin-theme-light");
    root.classList.add(isDarkTheme ? "admin-theme-dark" : "admin-theme-light");
  }, [isDarkTheme]);
  return null;
}

export function AdminThemeProvider({ navTheme, children }: AdminThemeProviderProps) {
  useAdminLocale();
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => getSystemPrefersDark());
  const validateMessages = buildAdminValidateMessages();

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    setSystemPrefersDark(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const effectiveNavTheme = resolveNavTheme(navTheme, systemPrefersDark);
  const isDarkTheme = effectiveNavTheme === "realDark";

  return (
    <ConfigProvider
      form={{ validateMessages }}
      theme={{
        cssVar: { key: "exam-admin" },
        algorithm: isDarkTheme ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      <ThemeClassSync isDarkTheme={isDarkTheme} />
      {children}
    </ConfigProvider>
  );
}

export function AdminLocaleSync({ children, publicConfig, setInitialState }: AdminLocaleSyncProps) {
  const locale = useAdminLocale();

  useEffect(() => {
    void setInitialState((state) => ({
      ...(state ?? {
        settings: defaultSettings,
        currentUser: null,
        publicConfig: publicConfig ?? null,
        localeVersion: locale,
      }),
      publicConfig: publicConfig ?? null,
      localeVersion: locale,
      settings: {
        ...(state?.settings ?? defaultSettings),
        title: publicConfig?.platform_name || formatMessage({ id: "common.brand.appName" }),
      },
    }));
  }, [locale, publicConfig, setInitialState]);

  return <>{children}</>;
}

export function AvatarDropdown({ children, currentUser, settings, localeVersion, onPreferenceApplied, onChangePassword, onLogout }: AvatarDropdownProps) {
  const [open, setOpen] = useState(false);

  const initialFormValues = useMemo<PreferenceFormValues>(
    () => ({
      localeVersion,
      navTheme: settings.navTheme === "realDark" || settings.navTheme === "system" ? settings.navTheme : "light",
      layout: (settings.layout as PreferenceFormValues["layout"]) ?? "mix",
      contentWidth: (settings.contentWidth as PreferenceFormValues["contentWidth"]) ?? "Fluid",
      fixedHeader: settings.fixedHeader ?? true,
      fixSiderbar: settings.fixSiderbar ?? true,
      splitMenus: settings.splitMenus ?? false,
      colorPrimary: settings.colorPrimary ?? "#1677ff",
      colorWeak: settings.colorWeak ?? false,
    }),
    [
      localeVersion,
      settings.colorPrimary,
      settings.colorWeak,
      settings.contentWidth,
      settings.fixSiderbar,
      settings.fixedHeader,
      settings.layout,
      settings.navTheme,
      settings.splitMenus,
    ],
  );

  const openPreferenceDrawer = () => {
    setOpen(true);
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "preferences",
      icon: <AdminShellIcon name="SettingOutlined" />,
      label: formatMessage({ id: "admin.shell.preferences.entry" }),
      onClick: openPreferenceDrawer,
    },
    {
      key: "change-password",
      icon: <AdminShellIcon name="LockOutlined" />,
      label: formatMessage({ id: "common.actions.changePassword" }),
      onClick: onChangePassword,
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <AdminShellIcon name="LogoutOutlined" />,
      label: formatMessage({ id: "common.actions.logout" }),
      onClick: onLogout,
    },
  ];

  if (!currentUser?.full_name) {
    return <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />;
  }

  return (
    <>
      <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["click"]}>
        <span className="admin-layout-avatar-dropdown">{children}</span>
      </Dropdown>
      {open ? (
        <Suspense fallback={null}>
          <LazyAdminPreferenceDrawer
            open={open}
            initialValues={initialFormValues}
            onClose={() => setOpen(false)}
            onPreferenceApplied={onPreferenceApplied}
          />
        </Suspense>
      ) : null}
    </>
  );
}

export function buildUnAccessibleNode() {
  return (
    <ResultView
      status="403"
      title="403"
      subTitle={formatMessage({ id: "admin.accessDenied.subtitle" })}
      extra={
        <Button color="primary" variant="solid" onClick={() => history.push(dashboardPath)}>
          {formatMessage({ id: "admin.accessDenied.backToDashboard" })}
        </Button>
      }
    />
  );
}

export const LazyDebugSettingDrawer = lazy(async () => ({
  default: (await import("@ant-design/pro-components")).SettingDrawer,
}));

export function normalizeNavThemeSetting(initialState: InitialState | undefined): NavThemePreference {
  return initialState?.settings?.navTheme === "realDark" || initialState?.settings?.navTheme === "system"
    ? initialState.settings.navTheme
    : "light";
}

export function toDebugDrawerSettings(initialState: InitialState | undefined, effectiveNavTheme: "light" | "realDark") {
  return { ...(initialState?.settings as LayoutSettings), navTheme: effectiveNavTheme };
}
